import { forwardRef, memo, type ButtonHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Badge, cn } from '@cyphex/ui';

export interface ControlButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon;
  label: string;
  /** Toggled on — brand tint (Chat open, hand raised). */
  active?: boolean;
  /** Muted / camera off — the control reads as "off", not as an error. */
  off?: boolean;
  badgeCount?: number;
  /** Something behind this control needs the user NOW (a guest in the lobby). */
  attention?: boolean;
  /** Opens a menu; the Dropdown primitive supplies the ARIA state. */
  hasMenu?: boolean;
}

/**
 * The meeting toolbar control: a 24px glyph over a 10px label, exactly as in
 * the approved call screen. Forwards its ref so it can be a Dropdown trigger.
 */
const ControlButtonBase = forwardRef<HTMLButtonElement, ControlButtonProps>(
  function ControlButtonBase(
    {
      icon: Icon, label, active = false, off = false, badgeCount, attention = false,
      hasMenu = false, className, ...rest
    },
    ref,
  ) {
    /* A button that is both "pressed" and "expanded" reads out two states for
       one thing. Menu triggers opt out of aria-pressed through `hasMenu`; a
       DISCLOSURE — the call's chat rail — opts out by supplying aria-expanded
       itself, and keeps `active` purely for the brand tint. */
    const discloses = rest['aria-expanded'] !== undefined;

    return (
      <button
        ref={ref}
        type="button"
        aria-pressed={hasMenu || discloses ? undefined : active}
        className={cn(
          'flex w-16 shrink-0 flex-col items-center gap-1 rounded-md px-2 py-1.5',
          'transition-colors duration-fast ease-emphasis hover:bg-subtle',
          'disabled:pointer-events-none disabled:opacity-40',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          active ? 'bg-primary-subtle text-primary' : 'text-secondary-foreground',
          off && 'text-destructive',
          className,
        )}
        {...rest}
      >
        <span className="relative flex h-6 items-center justify-center">
          <Icon size={24} strokeWidth={1.75} aria-hidden />
          {attention ? (
            <span className="absolute -left-3 -top-1.5 flex size-2.5" aria-hidden>
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-destructive opacity-60" />
              <span className="relative inline-flex size-2.5 rounded-full bg-destructive ring-2 ring-stage" />
            </span>
          ) : null}
          {badgeCount !== undefined && badgeCount > 0 ? (
            <Badge
              count={badgeCount}
              tone="brand"
              size="md"
              className="absolute -right-3 -top-1.5 ring-2 ring-stage"
            />
          ) : null}
        </span>
        <span className="text-2xs font-medium leading-none">{label}</span>
      </button>
    );
  },
);

export const ControlButton = memo(ControlButtonBase);
